import { theme } from '@kvdbil/components';
import { loadableReady } from '@loadable/component';
import 'core-js';
import 'intersection-observer';
import throttle from 'lodash.throttle';
import React, { useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import 'url-search-params-polyfill';
import AsyncRouter from '~/App/components/AsyncRouter';
import App from '~/App/index';
import { rehydrateLocale } from '~/App/shared/localization';
import { localeToPathSegment } from '~/App/shared/localization/helpers';
import {
  isDebug,
  launchDarklyClientID,
  targetedAuctionsFeatureEnabled
} from '~/config/public/environment';
import store from '~/config/store';
import { isLoggedIn } from '~/helpers/member';
import {
  getUsersTargetedAuctionGroupIds,
  pollTargetedAuctions,
  TARGETED_AUCTIONS_POLLING_INTERVALL_MILLISECONDS
} from '~/helpers/targetedAuctions';
import ChunkLoadErrorBoundary from './App/components/ChunkLoadErrorBoundary';
import { hydrateFavouriteAuctions } from './App/shared/actions/favouriteAuctions';
import { loginFlowOffCanvas } from './App/shared/actions/loginFlowActions';
import { recentlyViewedAuctionsHydrate } from './App/shared/actions/recently-viewed-auctions';
import { recentlyViewedSearchesHydrate } from './App/shared/actions/recently-viewed-searches';
import { fetchSavedSearches } from './App/shared/actions/saved-searches';
import {
  fetchSessionClientVersion,
  sessionHydrate
} from './App/shared/actions/session';
import {
  clearPollingTargetedAuctions,
  hydrateTargetedAuctions,
  setPollingTargetedAuctions
} from './App/shared/actions/targetedAuctionsActions';
import { hydrateWonAuctions } from './App/shared/actions/wonAuctions';
import DebugBar from './App/shared/components/DebugBar/DebugBar';
import { loadDayJSLocale } from './App/shared/date/date';
import { Session } from './App/shared/interfaces/store/Session';
import { BugsnagErrorBoundary } from './config/bugsnag';
import { requestErrorHandler } from '~/helpers/notifyError';
import { log } from '~/helpers/bugsnagHelper';
import { getRecoverableErrorGroupingHash } from '~/helpers/hydrationErrorGrouping';
import { LocalizationProvider } from './Locale';
import { Provider as GraphqlProvider } from 'urql';
// @ts-expect-error missing types
import Worker, { WorkerMessage } from './localForage.worker';
import { createGraphqlClient } from './config/graphqlClient';
import { FeatureFlagProvider } from './App/components/FeatureFlags';
import { createClient as createLDClient } from '@launchdarkly/js-client-sdk';
import { logger } from '~/helpers/logger';

require('~/../assets/styles.css');

const importFolder = (r: ReturnType<typeof require.context>) => {
  r.keys().forEach(r);
};

importFolder(
  require.context('../assets/images/', true, /\.(jpg|jpeg|gif|png|svg)$/)
);

importFolder(
  require.context('../assets/fonts/', true, /\.(woff(2)?|ttf|eot|svg)?$/)
);

const {
  localization: { domainLocale }
} = store.getState().app;

const currentLocale = window.CLIENT_LOCALE;
loadDayJSLocale(currentLocale);

// For easier debugging of redux state
window.reduxStore = store;

const localForageWorker = new Worker();

const hydrateSession = (session: Session) => {
  store.dispatch(clearPollingTargetedAuctions());
  store.dispatch(sessionHydrate({ session: session }));
  if (isLoggedIn(session)) {
    store
      .dispatch(hydrateFavouriteAuctions(session.auth.idToken))
      .catch(requestErrorHandler);
    store
      .dispatch(
        fetchSavedSearches({
          idToken: session.auth.idToken
        })
      )
      .catch(requestErrorHandler);
    store
      .dispatch(hydrateWonAuctions({ idToken: session.auth.idToken }))
      .catch(requestErrorHandler);

    if (targetedAuctionsFeatureEnabled) {
      const groups = getUsersTargetedAuctionGroupIds(session.auth.idToken);
      const threadArray: ReturnType<typeof setInterval>[] = [];

      groups.forEach(group => {
        pollTargetedAuctions(group, session);
        const thread = setInterval(
          () => pollTargetedAuctions(group, session),
          TARGETED_AUCTIONS_POLLING_INTERVALL_MILLISECONDS
        );

        threadArray.push(thread);
        store
          .dispatch(
            hydrateTargetedAuctions({
              idToken: session.auth.idToken,
              targetedAuctionId: group
            })
          )
          .then(() => {
            store.dispatch(setPollingTargetedAuctions(threadArray));
          })
          .catch((error: unknown) => {
            log(new Error('Failed to hydrate targeted auctios'), {
              error: error,
              severity: 'warning'
            });
          });
      });
    }

    store
      .dispatch(fetchSavedSearches({ idToken: session.auth.idToken }))
      .catch(requestErrorHandler);
    store
      .dispatch(hydrateFavouriteAuctions(session.auth.idToken))
      .catch(requestErrorHandler);
  }
};

const CLIENT_VERSION_CHECK_INTERVAL = 1000 * 60 * 15; // ms * sec * min = 15 minutes

const fetchClientVersion = () => {
  void store.dispatch(fetchSessionClientVersion());
};

const GET_SESSION = 'GET_SESSION';
const GET_RECENTLY_VIEWED_AUCTIONS = 'GET_RECENTLY_VIEWED_AUCTIONS';
const GET_RECENTLY_VIEWED_SEARCHES = 'GET_RECENTLY_VIEWED_SEARCHES';

localForageWorker.onmessage = ({ data: message }: { data: WorkerMessage }) => {
  switch (message.type) {
    case GET_RECENTLY_VIEWED_AUCTIONS:
      store.dispatch(recentlyViewedAuctionsHydrate(message.payload));
      break;
    case GET_RECENTLY_VIEWED_SEARCHES:
      store.dispatch(recentlyViewedSearchesHydrate(message.payload));
      break;
    case GET_SESSION:
      hydrateSession(message.payload);
      break;
    default:
      break;
  }
};

localForageWorker.onerror = (error: object) => {
  logger.error('Error in localForageWorker', { error });
};

// Only update localForage from store every {throttleTimeout} milliseconds
const throttleTimeout = 1000;

const SET_SESSION = 'SET_SESSION';
const SET_SELL_FLOW = 'SET_SELL_FLOW';
const SET_RECENTLY_VIEWED_AUCTIONS = 'SET_RECENTLY_VIEWED_AUCTIONS';
const SET_RECENTLY_VIEWED_SEARCHES = 'SET_RECENTLY_VIEWED_SEARCHES';
store.subscribe(
  throttle(() => {
    const storeState = store.getState();
    const { isHydrated } = storeState.session;

    const isRecentlyAuctionsHydrated =
      storeState.recentlyViewedAuctions.isHydrated;
    const isRecentlySearchesHydrated =
      storeState.recentlyViewedSearches.isHydrated;

    if (isHydrated) {
      localForageWorker.postMessage({
        type: SET_SESSION,
        payload: storeState.session
      });
    }

    if (isRecentlyAuctionsHydrated) {
      try {
        localForageWorker.postMessage({
          type: SET_RECENTLY_VIEWED_AUCTIONS,
          payload: storeState.recentlyViewedAuctions
        });
      } catch (e: unknown) {
        // I don't think this will actually happen, as the data in the store
        // _should_ not be large enough to cause an out of memory error after
        // the changes to the reducer. Log just in case
        log(new Error('Failed saving recently viewed searches in browser'), {
          severity: 'warning',
          error: e,
          payload: storeState.recentlyViewedAuctions
        });
      }
    }

    if (isRecentlySearchesHydrated) {
      localForageWorker.postMessage({
        type: SET_RECENTLY_VIEWED_SEARCHES,
        payload: storeState.recentlyViewedSearches
      });
    }

    localForageWorker.postMessage({
      type: SET_SELL_FLOW,
      payload: storeState.sellFlow
    });
  }, throttleTimeout)
);

const PostHydrationBootstrap = () => {
  useEffect(() => {
    store.dispatch(loginFlowOffCanvas(window.innerWidth > 768));

    localForageWorker.postMessage({ type: GET_RECENTLY_VIEWED_AUCTIONS });
    localForageWorker.postMessage({ type: GET_RECENTLY_VIEWED_SEARCHES });
    localForageWorker.postMessage({ type: GET_SESSION });

    fetchClientVersion();
    const clientVersionCheck = setInterval(
      fetchClientVersion,
      CLIENT_VERSION_CHECK_INTERVAL
    );

    return () => clearInterval(clientVersionCheck);
  }, []);

  return null;
};

const handleNavigationBlock = (message: string, callback: CallableFunction) =>
  callback(message !== 'block');

const flags = window.FEATURE_FLAGS.flags;
const user = window.FEATURE_FLAGS.user;
const bootstrap = window.FEATURE_FLAGS.bootstrap;

const client = createLDClient(launchDarklyClientID, user, {
  sendEvents: true, // False will send no events, A/B test events included
  withReasons: false, // Will request evaluation reasons from LD, no need since we got them from backend
  fetchGoals: false, // One less request, but will only allow custom metrics being counted
  diagnosticOptOut: true, // Will not send any diagnostic data to LD
  disableCache: true // v4 caches flags in localStorage by default, we bootstrap from the server instead
});

void client
  .start({ bootstrap }) // Set flags from server side, skips initial get flags request on init
  .then(result => {
    if (result.status !== 'complete') {
      logger.error('LaunchDarkly client failed to start', result);
    }
  });

const routerBasename =
  domainLocale !== currentLocale
    ? `/${localeToPathSegment(currentLocale)}`
    : undefined;

const { value: cmsProxyCaching } = flags?.['cms-proxy-caching'] ?? {};

const createDoc = () => {
  return (
    <BugsnagErrorBoundary>
      <GraphqlProvider
        value={createGraphqlClient({
          useExternalCaching: Boolean(cmsProxyCaching)
        })}
      >
        <LocalizationProvider
          locale={rehydrateLocale()}
          options={{ showKeys: false }}
        >
          <ThemeProvider theme={theme}>
            <Provider store={store}>
              <PostHydrationBootstrap />
              <FeatureFlagProvider flags={flags} user={user} client={client}>
                <ChunkLoadErrorBoundary>
                  <Router
                    basename={routerBasename}
                    getUserConfirmation={handleNavigationBlock}
                  >
                    <AsyncRouter store={store}>
                      <App />
                    </AsyncRouter>
                  </Router>
                </ChunkLoadErrorBoundary>
              </FeatureFlagProvider>
            </Provider>
          </ThemeProvider>
          {isDebug && <DebugBar />}
        </LocalizationProvider>
      </GraphqlProvider>
    </BugsnagErrorBoundary>
  );
};

void loadableReady(() => {
  const container = document.getElementById('root');

  if (!container) {
    throw new Error('Root element #root not found');
  }

  let hasReportedRecoverableError = false;

  hydrateRoot(container, createDoc(), {
    onRecoverableError: (error, errorInfo) => {
      if (hasReportedRecoverableError) {
        return;
      }
      hasReportedRecoverableError = true;

      const componentStack = errorInfo?.componentStack ?? '';

      log(error, {
        severity: 'warning',
        componentStack: componentStack || 'unknown',
        groupingHash:
          error instanceof Error
            ? getRecoverableErrorGroupingHash(error, componentStack)
            : undefined
      });
    }
  });
});
